// What a diary entry is filed under, in words a person reads.
//
// Beats arrive keyed two different ways: by ACTIVITY (cinema, forage) and
// by PLACE, where the key is the scene's own POI name. Those are variable
// names, and they were rendering raw, so the diary showed "eatJunk",
// "surfInternet" and, the one that gave the game away, "tagging_04" as
// the heading on an entry about rummaging in bins. Sam: "it's too codey,
// we don't want to let on that I added in specific tagging walls."
//
// Pure and separate from the component so it can be tested, because the
// failure mode here is silent: an unmapped key still renders, it just
// renders an identifier.

import { PLACE_ACTIONS } from '../world/placeActions.js'

const ACTIVITY_LABELS = {
  cinema: 'Cinema',  gamble: 'Casino',  internet: 'Internet',  rave: 'Rave',
  forage: 'Food',  scavenge: 'Junk heap',  pond: 'The pond',  bar: 'The bar',
  // the monorail station, up the lift at the east end of the tall flats (sim-server world/Tickets.js)
  tickets: 'The monorail station',
  laundrette: 'Laundrette',  museum: 'Museum',  salon: 'Salon',
  tattoo: 'Tattoo parlour',  phone: 'Payphone',  lookout: 'Lookout',
  inspect: 'The shrine',  town: 'Around town',  studio: 'The studio',
  trip: 'The mushrooms',
  trades: 'Trading',
  hobby: 'His own thing',
  // Somebody left him something and he decided about it, which is the most
  // personal thing in the product, and it was filed under the name of a
  // database column. Same class as the "Gift / wanting" heading, missed
  // because offerings arrive by a different path.
  text: 'An offering',
  image: 'An offering',
  gift: 'A gift',
  // What he carries (sim-server world/things/bag.js). Its moments are filed
  // under the move's own name, which has an underscore and so read "Around
  // town" for all three: two of them happen at his roost.
  put_away: 'His roost',
  take_out: 'His roost',
  pick_up: 'Around town',
  // The morning entry: how the night went, and what it points back at.
  wake: 'Morning',
  // He knows something is coming and says so. The rarest kind.
  ahead: 'Looking ahead',
  // The instrument he is learning. The label stays plain; the entries
  // carry the arc.
  piano: 'The piano',
  // His music's other parts, which fell through to the last resort and read "Rack", "Drums" and "Desk": Sam, 25 Oct,
  // "it could just mean a desk you work at". The desk is where a piece of his music is put together from its parts.
  rack: 'The synth',
  drums: 'The drum machine',
  desk: 'Arranging music',
  garden: 'The greenhouse',
  // A visitor that will never come back, on its way out.
  octopus: 'The pond visitor',
  // Falling asleep, he notices what the day kept returning to.
  dayend: "Day's end",
  // What he dreamt, the mind's own, once a night (sim-server Dreamer.js).
  dream: 'Asleep',
  // The scene's own POI names, which reach this label whenever a beat is
  // filed by PLACE rather than by activity. They are variable names and
  // they were rendering raw: a reader got "eatJunk", "surfInternet" and,
  // the one that gave the game away, "tagging_04" on an entry about
  // rummaging in bins. The sim no longer files anything under an internal
  // marker; this covers the rest of the vocabulary.
  drinkAlcohol: 'The bar',  drinkCoffee: 'Coffee shop',
  eatChinese: 'The takeaway',  eatChinese_2: 'The takeaway',
  eatBakery: 'The bakery',     eatPizza: 'The pizza place',
  eatSoup: 'The soup cart',
  // The street south of the pizza place (7 Oct), by stand spot.
  eatJapanese: 'The japanese restaurant',  eatBakeryEarly: 'The early bakery',
  eatTaco: 'The taco truck',  shopMags: 'City Mags',
  shopGallery: 'The art gallery',  shopWorkshop: 'The workshop',  shopTech: 'The tech shop', jobCentre: 'The job centre',
  // The places he asked for at the shrine: their beats are filed under the
  // place itself (the sim's Wishes.js), and their stand spots by POI name.
  shelter: 'The shelter',  bench: 'The bench',
  library: 'The library corner',  keyroom: 'The keys',  workbench: 'The workshop',
  stage: 'The stage',  market: 'The night market',
  wishShelter: 'The shelter',  wishBench: 'The bench',
  wishLibrary: 'The library corner',  wishKeys: 'The keys',  wishWorkshop: 'The workshop',
  wishStage: 'The stage',  wishMarket: 'The night market',
  eatHealth: 'The apple tree',  eatJunk: 'The burger place',
  eatMushroom_1: 'The mushrooms',  eatMushroom_2: 'The mushrooms',
  eatMushroom_3: 'The mushrooms',  eatMushroom_4: 'The mushrooms',
  laundry: 'Laundrette',  surfInternet: 'Internet cafe',
  watchMovie: 'Cinema',  greenStone: 'The green stone',  green_stone: 'The green stone',
  pond_paddle: 'The pond',  shrine: 'The shrine',  artWall: 'The wall',
  rest: 'His roost',  rest_2: 'His roost',  rest_3: 'His roost',  rest_4: 'His roost',
  rest_5: 'His roost',
}

// Last resort. A label is a heading a person reads, so it must never be
// an identifier: anything unmapped, and anything that still looks like a
// variable, says where he was in the vaguest honest way rather than
// exposing how the world is wired.
export function labelFor(kind) {
  const known = ACTIVITY_LABELS[kind] || PLACE_ACTIONS[kind]?.title
  if (known) return known
  if (!kind || /[_\d]|[a-z][A-Z]/.test(String(kind))) return 'Around town'
  return String(kind).charAt(0).toUpperCase() + String(kind).slice(1)
}

export { ACTIVITY_LABELS }
